/** @jsx isolateComponent */

/* eslint-disable import/no-duplicates */
import type { ReactNode } from 'react';
import React from 'react';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Button, useTheme } from '@zapier/design-system';

import UniversalNavLink from '../UniversalNavLink';
import { usageLink } from '../../constants';
import { useUniversalNavContext } from '../../hooks';

import UniversalSidebarError from './UniversalSidebarError';
import UniversalSidebarSubheaderButton from './UniversalSidebarSubheaderButton';
import UniversalSidebarUsage from './UniversalSidebarUsage/UniversalSidebarUsage';
import useSidebarData from './utils/useSidebarData';
import UniversalSidebar, { isLinkSelected } from './UniversalSidebar';
import type { Props as UniversalSidebarProps } from './UniversalSidebar';

const infinitize = (number: number) => {
  return number === -1 ? Infinity : number;
};

type Props = Omit<
  UniversalSidebarProps,
  | 'isFullWidth'
  | 'isPinned'
  | 'links'
  | 'logoAriaLabel'
  | 'logoHref'
  | 'renderFooter'
  | 'skipLinkHref'
> & {
  /**
   * The `id` of the account the user is currently switched to.
   */
  currentAccountId: number;
};

type MaybeCtaProps = {
  numTasks: number;
  numTasksMax: number;
  numZaps: number;
  numZapsMax: number;
  planAmount: number;
};

const MaybeCta = (props: MaybeCtaProps) => {
  const isFree = props.planAmount === 0;
  const isNearTaskLimit = props.numTasks / props.numTasksMax >= 0.8;
  const isNearZapLimit = props.numZaps / props.numZapsMax >= 0.8;
  return isFree || isNearTaskLimit || isNearZapLimit ? (
    <Button
      color="secondary"
      href="/app/billing/plans"
      isFullWidth={true}
      size="small"
    >
      Upgrade plan
    </Button>
  ) : null;
};

const Styles = labelStyles('UniversalSidebarContainer', {
  footerWrapper: (isRebrand: boolean) => ({
    overflow: 'hidden',
    ...(!isRebrand && { padding: 10 }),
  }),
});

const FooterWrapper = ({
  children,
  isRebrand,
}: {
  children: ReactNode;
  isRebrand: boolean;
}) => <div css={Styles.footerWrapper(isRebrand)}>{children}</div>;

/**
 * The hooked up `UniversalSidebar` component. Hooked up to data,
 * performs business logic, etc.
 */
const UniversalSidebarContainer = ({ currentAccountId, ...props }: Props) => {
  const { useQuery } = useUniversalNavContext();
  const result = useSidebarData({ accountId: currentAccountId, useQuery });
  const { loading, data, error } = result;
  const { isRebrand } = useTheme();

  const renderedUsageLink = (
    <UniversalNavLink
      href={usageLink.href}
      icon={usageLink.icon}
      isCollapsed={props.isCollapsed}
      isSelected={isLinkSelected(usageLink.href || '', props.selectedHref)}
      isSidebar={true}
      text={usageLink.text}
    />
  );

  const renderFooter = () => {
    if (props.isCollapsed) {
      return renderedUsageLink;
    }
    if (error) {
      return (
        <React.Fragment>
          {renderedUsageLink}
          <FooterWrapper isRebrand={isRebrand}>
            <UniversalSidebarError>{error.message}</UniversalSidebarError>
          </FooterWrapper>
        </React.Fragment>
      );
    }
    if (loading || !data?.account) {
      return null;
    }
    const { plan, usage } = data.account;
    const tiers = plan.tiers;
    const freeTier = tiers && tiers.find((tier) => tier.pricePerTask === 0);
    const numFreeTasksMax = freeTier ? freeTier.numTasks : 0;
    const normalizedUsage = {
      isPlanMetered: plan.hasMeteredBilling,
      numTasks: usage.tasks.total,
      numFreeTasksMax,
      numTasksMax: infinitize(usage.tasks.limit),
      numZaps: usage.zaps.on,
      numZapsMax: infinitize(usage.zaps.limit),
    };
    return (
      <FooterWrapper isRebrand={isRebrand}>
        <UniversalSidebarUsage
          {...normalizedUsage}
          resetDate={usage.periodEnd}
          title={`${plan.name} Plan`}
        >
          <MaybeCta {...normalizedUsage} planAmount={plan.amount} />
        </UniversalSidebarUsage>
      </FooterWrapper>
    );
  };

  return (
    <UniversalSidebar
      {...props}
      logoAriaLabel="Go to dashboard"
      logoHref="/app/dashboard"
      renderFooter={renderFooter}
      renderSubheader={() => (
        <UniversalSidebarSubheaderButton
          href="/webintent/create-zap?entry-point-method=make_a_zap_call_to_action"
          iconName="formAdd"
          isCollapsed={props.isCollapsed}
          text="Create Zap"
        />
      )}
    />
  );
};

export default UniversalSidebarContainer;
